/**
 * 별도 OS 창(팝업)을 열고 그 안에 React 를 포털로 그릴 준비를 한다 — 편집기·결과 「창 분리」(DetachablePanel)와
 * 팝업 창 「새 창으로」(useModalWindow, 2026-10-06)가 같이 쓴다. 두 벌로 두면 한쪽만 스타일·다크모드가 어긋난다.
 *
 * 같은 출처(about:blank)라 부모의 React 가 포털로 그대로 그린다 — 상태·이벤트가 한 앱 안에 있다. 그래서 창을 다른 모니터로
 * 옮겨도 데이터는 그대로이고, 닫으면 앱 안으로 돌아온다. 스탠드얼론(Electron)은 setWindowOpenHandler 가 http 가 아닌 창을 허용한다.
 */
import { attachDocument as attachZoomDocument } from './appZoom';

/** 부모 문서의 스타일을 팝업에 복사한다 — 지금 있는 것 + **나중에 붙는 것**(개발 모드·지연 로드 화면의 CSS) */
export function copyStyles(popup) {
    const clone = (node) => {
        try {
            if (node.tagName === 'LINK' && node.rel === 'stylesheet') {
                const l = popup.document.createElement('link');
                l.rel = 'stylesheet'; l.href = node.href;
                popup.document.head.appendChild(l);
            } else if (node.tagName === 'STYLE') {
                const st = popup.document.createElement('style');
                st.textContent = node.textContent;
                popup.document.head.appendChild(st);
            }
        } catch (_e) { /* 창이 닫히는 중 */ }
    };
    document.querySelectorAll('link[rel="stylesheet"], style').forEach(clone);
    // 열린 뒤에 붙는 스타일도 따라간다 — 안 그러면 처음 여는 화면의 CSS 가 팝업에만 없다
    const mo = new MutationObserver((list) => {
        if (popup.closed) { mo.disconnect(); return; }
        for (const m of list) m.addedNodes.forEach(n => { if (n.nodeType === 1) clone(n); });
    });
    mo.observe(document.head, { childList: true });
    return () => mo.disconnect();
}

/** 부모의 다크/라이트 전환을 팝업에도 */
export function syncTheme(popup) {
    const apply = () => {
        if (popup.closed) return;
        popup.document.documentElement.setAttribute('data-theme', document.documentElement.getAttribute('data-theme') || 'light');
    };
    apply();
    const mo = new MutationObserver(apply);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
}

/** 열려 있는 분리 창들 — 확인·입력 대화상자를 **그 창 안에** 띄우려고(dialogHost) */
const OPEN = new Set();

/**
 * 본창이 가려진 동안 애니메이션 프레임을 분리 창에서 받는다(2026-11-02 신고 — 분리한 그래프에 마우스를 올려도 값·기준선이 안 떴다).
 *
 * 분리 창의 화면도 코드는 본창의 것이라 전역 `requestAnimationFrame` 은 **본창의** 것이다. 분리 창을 크게 띄워 본창을 덮거나 본창을
 * 최소화하면 브라우저가 본창을 「숨김」 으로 보고 프레임을 멈춘다 — recharts 3 은 마우스 이동을 프레임에 맞춰 처리하므로(eventSettings
 * throttleDelay 'raf') 호버가 영영 처리되지 않았다. 끌어 고르기(onMouseMove)·그래프 애니메이션도 같은 길이다.
 * 라이브러리마다 고치지 않고 여기 한 곳에서: 본창이 숨김이고 보이는 분리 창이 있으면 그 창의 프레임에 태운다. 본창이 보이면 원래 그대로다.
 * 콜백에는 **본창의** performance.now() 를 준다 — 창마다 시간 기준이 달라 섞이면 애니메이션 시간이 뛴다.
 */
const rafBridge = {
    installed: false, origRaf: null, origCaf: null, raf: null, caf: null, seq: 1e9, pending: new Map(),
};
function frameHost() {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return null;
    for (const p of OPEN) {
        try { if (!p.closed && p.document.visibilityState !== 'hidden') return p; } catch (_e) { /* 닫히는 중 */ }
    }
    return null;
}
function installRafBridge() {
    // 설치한 것이 그 사이 다른 누구에게 덮였으면 다시 놓는다
    if (rafBridge.installed && window.requestAnimationFrame !== rafBridge.raf) { rafBridge.installed = false; rafBridge.pending.clear(); }
    if (rafBridge.installed || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return;
    rafBridge.installed = true;
    rafBridge.origRaf = window.requestAnimationFrame;
    rafBridge.origCaf = window.cancelAnimationFrame;
    const run = (id, cb) => () => {
        if (!rafBridge.pending.delete(id)) return;
        cb(window.performance.now());
    };
    rafBridge.raf = function bridgedRaf(cb) {
        const id = ++rafBridge.seq;
        const host = frameHost();
        const fire = run(id, cb);
        const nativeId = host ? host.requestAnimationFrame(fire) : rafBridge.origRaf.call(window, fire);
        rafBridge.pending.set(id, { win: host, nativeId, fire });
        return id;
    };
    rafBridge.caf = function bridgedCaf(id) {
        const e = rafBridge.pending.get(id);
        if (!e) { rafBridge.origCaf.call(window, id); return; }   // 다리를 놓기 전에 받은 번호
        rafBridge.pending.delete(id);
        try { if (e.win) e.win.cancelAnimationFrame(e.nativeId); else rafBridge.origCaf.call(window, e.nativeId); } catch (_e) { /* 닫힌 창 */ }
    };
    window.requestAnimationFrame = rafBridge.raf;
    window.cancelAnimationFrame = rafBridge.caf;
}
/** 닫히는 분리 창에 걸어 둔 프레임은 본창으로 옮긴다(안 옮기면 그 콜백이 영영 안 불려 기다리는 쪽이 멈춘다). 마지막 창이면 다리를 걷는다 */
function releaseRafBridge(popup) {
    if (!rafBridge.installed) return;
    for (const e of rafBridge.pending.values()) {
        if (e.win === popup) { e.win = null; e.nativeId = rafBridge.origRaf.call(window, e.fire); }
    }
    if (OPEN.size > 0 || rafBridge.pending.size > 0) return;
    // 그 사이 다른 누가 갈아 끼웠으면(테스트의 가짜 타이머 등) 그것을 덮지 않는다
    if (window.requestAnimationFrame === rafBridge.raf) window.requestAnimationFrame = rafBridge.origRaf;
    if (window.cancelAnimationFrame === rafBridge.caf) window.cancelAnimationFrame = rafBridge.origCaf;
    rafBridge.installed = false;
}

/**
 * 공통 대화상자(useConfirm·usePrompt)를 그릴 곳 — 지금 포커스가 있는 분리 창의 body, 없으면 앱 창의 body(2026-10-11).
 * 분리 창에서 「삭제할까요?」 를 물을 때 대화상자가 뒤에 숨은 앱 창에 뜨면 창이 멈춘 것처럼 보인다.
 */
export function dialogHost() {
    for (const p of OPEN) {
        try { if (!p.closed && p.document.hasFocus()) return p.document.body; } catch (_e) { /* 닫히는 중 */ }
    }
    return document.body;
}

/**
 * 팝업을 연다.
 * @returns {{ popup, container, close }} 또는 null(팝업 차단)
 *   onClosed — 사용자가 창을 닫으면(우리가 close() 한 것은 제외) 한 번 부른다
 */
export function openPopout({ title, width = 1200, height = 800, onClosed }) {
    const left = window.screenX + 60;
    const top = window.screenY + 40;
    const popup = window.open('', `livesql_${String(title).replace(/\W+/g, '_')}_${Date.now()}`,
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    if (!popup) return null;
    popup.document.open();
    popup.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"></head>'
        + '<body style="margin:0;padding:0;overflow:hidden;"><div id="popout-root" style="height:100%;width:100%;overflow:hidden;"></div></body></html>');
    popup.document.close();
    popup.document.title = `LiveSQL - ${title}`;
    const undoStyles = copyStyles(popup);
    const undoTheme = syncTheme(popup);
    const base = popup.document.createElement('style');
    base.textContent = `html, body { margin: 0; height: 100%; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      [data-theme="dark"] body { background: #1e1e1e; color: #d4d4d4; }`;
    popup.document.head.appendChild(base);
    // 화면 확대/축소 — 분리한 창에서도 Ctrl+휠·단축키가 앱 배율을 바꾼다(배율 자체는 main 이 이 창에도 건다)
    const undoZoom = attachZoomDocument(popup.document);

    let closedByUs = false;
    let done = false;
    const finish = () => {
        if (done) return;
        done = true;
        OPEN.delete(popup);
        releaseRafBridge(popup);
        clearInterval(timer);
        undoStyles(); undoTheme(); undoZoom();
        if (!closedByUs && onClosed) onClosed();
    };
    OPEN.add(popup);
    installRafBridge();
    popup.addEventListener('beforeunload', finish);
    // 폴백 — beforeunload 를 놓치는 경우(Electron 창 강제 종료 등)
    const timer = setInterval(() => { if (popup.closed) finish(); }, 500);
    return {
        popup,
        container: popup.document.getElementById('popout-root'),
        close: () => { closedByUs = true; finish(); try { popup.close(); } catch (_e) { /* 이미 닫힘 */ } },
    };
}
